<template>
  <div class="forgot-password tw:py-6 tw:px-5 tw:bg-white">
    <h2 v-if="errors.timeout" class="confirm-email__alert">
      {{ showAlert(errors.timeout, 3000, 'error') }}
    </h2>
    <h1 class="forgot-password__title">Reset Password</h1>
    <p v-if="!isRequestSend" class="forgot-password__subtitle">
      Enter your email and we’ll send you a reset link.
    </p>
    <form
      v-if="!isRequestSend"
      class="forgot-password__form"
      novalidate
      @submit.prevent="handleForm"
    >
      <input
        id="email"
        v-model.trim="inputData.email"
        class="input"
        name="email"
        type="email"
        @blur="activeInputName = null"
        @input="handleInput"
        @change="handleInput"
        @keyup.delete="handleInput"
      />
      <img
        v-if="
          isEmailValid &&
          inputData.email.length > 0 &&
          checkInputForErrors('emailError') &&
          checkInputForErrors('emailValidationError')
        "
        class="forgot-password__email-check"
        src="../../assets/images/greenCheck.svg"
        alt=""
      />
      <img
        v-if="isEmailValid === false && inputData.email.length > 0"
        class="forgot-password__email-check"
        src="../../assets/images/cross.svg"
        alt=""
        @click="clearInputAndErrors('email')"
      />
      <label
        for="email"
        class="label"
        :class="{ activeInput: inputData.email !== isEmptyInput || activeInputName === 'email' }"
      >
        Email
      </label>
      <span id="emailError" class="forgot-password__error">
        <img
          class="forgot-password__error-cross"
          src="../../assets/images/cross.svg"
          alt="cross"
          @click="clearInputAndErrors('email')"
        />
        <span class="forgot-password__error-text">Required field</span>
      </span>
      <span id="emailValidationError" class="forgot-password__error">
        <img
          class="forgot-password__error-cross"
          src="../../assets/images/cross.svg"
          alt="cross"
          @click="clearInputAndErrors('email')"
        />
        <span class="forgot-password__error-text">Please enter a valid email address</span>
      </span>
      <AuthActionButton
        full-width
        variant="primary"
        class="forgot-password__btn-submit"
        :show-icon="false"
        button-type="submit"
        text="Send Instructions"
        :disabled="!isSubmitEnabled"
      />
      <div class="forgot-password__link-to-login-wrapper">
        <p class="forgot-password__text">Back to the</p>
        <b-nav-item to="/sign-in" class="forgot-password__link--orange"> Sign In </b-nav-item>
        <p class="forgot-password__text">page</p>
      </div>
    </form>
    <div v-else class="forgot-password__message">
      <p class="forgot-password__message-text">
        <template v-if="ssoMessage">{{ ssoMessage }}</template>
        <template v-else>
          If specified email is registered, you'll
          <br />
          receive an email with password reset
        </template>
      </p>
      <div class="forgot-password__back-inline">
        <p class="forgot-password__text">Back to the</p>
        <b-nav-item to="/sign-in" class="forgot-password__link--orange"> Sign In </b-nav-item>
        <p class="forgot-password__text">page</p>
      </div>
    </div>
    <div v-if="isLoading" class="forgot-password__spinner">
      <Spinner />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/Spinner';
import AuthActionButton from '@/_core/shared/ui/AuthActionButton/AuthActionButton.vue';
import { store } from '@/store';
import {
  checkFormInputsForCompleteness,
  checkFormInputsForErrors,
  checkEmailValidation,
  checkInputForErrors,
  validateEmail,
  hideErrors,
} from '@/services/functions';
import { mapGetters } from 'vuex';

export default {
  components: {
    Spinner,
    AuthActionButton,
  },
  data() {
    return {
      isRequestSend: false,
      ssoMessage: null,
      inputData: {
        email: '',
      },
      isLoading: false,
      isEmailValid: null,
      isEmptyInput: '',
      activeInputName: null,
    };
  },
  computed: {
    ...mapGetters({
      errors: 'authentication/errors',
    }),
    isSubmitEnabled() {
      return validateEmail(this.inputData.email);
    },
  },
  methods: {
    checkInputForErrors,
    clearInputAndErrors(inputField) {
      this.inputData.email = '';
      hideErrors(['emailError', 'emailValidationError']);
      document.getElementById(inputField).style.border = '1px solid #F0F0F0';
    },
    async handleForm() {
      store.commit('authentication/clearErrors');

      const errorIds = ['emailError', 'emailValidationError'];
      hideErrors(errorIds);

      if (this.inputData.email) {
        checkEmailValidation(this.inputData, 'forgot-password__input--validation-error');
      }

      checkFormInputsForErrors(this.inputData, 'forgot-password__input--error');

      if (checkFormInputsForCompleteness(this.inputData) && validateEmail(this.inputData.email)) {
        await this.sendRequest({ email: this.inputData.email });
      }
    },
    async sendRequest(payload) {
      try {
        this.isLoading = true;
        this.ssoMessage = null;
        const res = await this.$auth.sendForgotPassword(payload);
        this.isLoading = false;
        if (!Object.keys(this.errors).length) {
          this.isRequestSend = true;
          if (res?.ssoAccount && res?.ssoMessage) this.ssoMessage = res.ssoMessage;
        }
      } catch (error) {
        this.isLoading = false;
        console.log(error);
      }
    },
    handleInput(e) {
      this.checkKeyEvent(e);
      this.isEmailValid = validateEmail(this.inputData.email);

      hideErrors(['emailError', 'emailValidationError']);
      document.getElementById('email').style.border = '1px solid #F0F0F0';
      store.commit('authentication/clearErrors');
    },
    checkKeyEvent(event) {
      this.activeInputName = event.target.id;
    },
    showAlert(message, timer, status) {
      this.$swal({
        toast: true,
        position: 'top',
        showConfirmButton: false,
        icon: status,
        title: message,
        timer: timer,
        timerProgressBar: true,
      });
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

.forgot-password {
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;

  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
    margin: 0;
  }

  &__subtitle {
    @include Aspekta-font(500, 14px, 150%, #706969);
    margin: 12px 0 30px;
    text-align: center;
    max-width: 460px;
  }

  &__form {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 395px;
    margin-bottom: 40px;
  }

  &__message {
    width: 100%;
    max-width: 395px;
    margin: 0 auto;
  }

  &__input {
    &--error,
    &--validation-error {
      border: 1px solid #fb8d62;
      border-radius: 5px;
    }
  }

  &__text {
    margin: 0;
    @include Aspekta-font(500, 14px, 150%, #706969);

    &--margin {
      margin-right: 5px;
    }

    &--center {
      text-align: center;
      white-space: pre-line;
    }
  }

  &__message-text {
    @include Aspekta-font(500, 14px, 150%, #706969);
    text-align: center;
    margin: 10px 0 0;
    white-space: pre-line;
  }

  &__link--orange {
    display: inline-flex;
    align-items: center;
    font-family: Cabin, serif;

    .nav-link {
      margin: 0;
      padding: 0;
    }

    a {
      @include Aspekta-font(500, 14px, normal, #ffb400);
      font-size: 14px !important;
      color: #ffb400;

      &:hover {
        color: #ff7a08;
        text-decoration: underline;
      }
    }
  }

  &__link-to-login-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 6px;
    margin-top: 12px;
  }

  &__btn-submit {
    margin: 0;
  }

  &__back-inline {
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;
  }

  &__error {
    width: 100%;
    max-width: 831px;
    position: relative;
  }

  &__error-cross {
    position: absolute;
    top: -57px;
    right: 20px;
    cursor: pointer;
    width: 10px;
    height: 10px;
    object-fit: contain;
  }

  &__error-text {
    @include Aspekta-font(500, 10px, 150%, #fb8d62);
    position: absolute;
    top: -30px;
    left: 3px;
    margin: 0;
  }

  &__email-check {
    position: absolute;
    top: 17px;
    right: 20px;
    cursor: pointer;
    width: 10px;
    height: 10px;
    object-fit: contain;
  }
}

#emailError,
#emailValidationError,
#passwordError {
  display: none;
}

@media (max-width: 768px) {
  .forgot-password {
    padding: 0 20px;

    &__title {
      margin: 30px 0 0;
      text-align: center;
    }

    &__subtitle {
      margin: 8px 0 8px;
    }

    &__form {
      margin-bottom: 16px;
    }

    &__back-inline {
      margin-top: 16px;
    }
  }
}
</style>
